import { Clipboard } from '@angular/cdk/clipboard';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  HostBinding,
  OnInit,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import Fuse from 'fuse.js';
import { DeviceDetectorService } from 'ngx-device-detector';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { filter, map, pluck, shareReplay, startWith, take } from 'rxjs/operators';
import { AuthService, LoHiUserInfo } from 'src/app/root/auth.service';
import { DriversService } from 'src/app/root/drivers.service';
import { SnackBarService } from 'src/app/shared/snackbar.service';
import { ActiveDriver, DriverStat, isActiveDriver, LoHiLoadsListItem, PendingDriver } from 'src/app/types';
import { fuse } from 'src/app/utilities/fuse';
import { RouterStateService } from '../../root/router-state.service';

const searchOptions: Fuse.IFuseOptions<LoHiLoadsListItem> = {
  distance: 100,
  keys: ['id', 'name', 'uniqueId', 'phone'],
  location: 0,
  minMatchCharLength: 1,
  shouldSort: true,
  threshold: 0.2,
};

interface MessageDetails {
  driverPhoneNumber: string;
  suggestedText: string;
  url: string;
  urlNote: string;
}

@Component({
  selector: 'lohi-driver-details',
  templateUrl: './driver-details.component.html',
  styles: [],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DriverDetailsComponent implements OnInit {
  @ViewChild('coaching') public temp: TemplateRef<any>;
  @ViewChild('removeUserModal') public removeUserModal: TemplateRef<any>;
  @HostBinding('class.overflow-hidden') public noScroll = true;
  public currentDriver$: Observable<ActiveDriver>;
  public currentPendingDriver$: Observable<PendingDriver>;
  public currentDriverCompletedLoads$: Observable<LoHiLoadsListItem[]>;
  public userInfo$: Observable<LoHiUserInfo>;
  public driverCutFormControl: FormControl;
  public searchForm = new FormControl(null);
  public coachingStatus = 0;
  public messageDetails$$ = new BehaviorSubject<MessageDetails>(null);
  public backUrl$: Observable<string[]>;
  public unsetPriorityFormControl: FormControl;
  public setPriorityFormControl: FormControl;
  public driverStats$: Observable<DriverStat>;

  constructor(
    private driversService: DriversService,
    private authService: AuthService,
    private snackBar: SnackBarService,
    private router: Router,
    private matDialog: MatDialog,
    private routerState: RouterStateService,
    private cd: ChangeDetectorRef,
    private deviceService: DeviceDetectorService,
    private clipboard: Clipboard,
  ) {}

  public ngOnInit(): void {
    this.backUrl$ = this.routerState.backUrl$.pipe(take(1));
    this.currentDriver$ = this.driversService.currentDriver$.pipe(
      filter((driver) => !!driver),
      map((driver) => (isActiveDriver(driver) ? driver : null)),
      shareReplay(1),
    );
    this.currentPendingDriver$ = this.driversService.currentDriver$.pipe(
      filter((driver) => !!driver),
      map((driver) => (!isActiveDriver(driver) ? driver : null)),
      shareReplay(1),
    );
    this.currentDriverCompletedLoads$ = combineLatest([
      this.driversService.currentDriverCompletedLoads$.pipe(map(fuse(searchOptions))),
      this.searchForm.valueChanges.pipe(startWith('')),
    ]).pipe(
      map(([fused, searchTerm]) => {
        if (!fused) {
          return [];
        }
        if (!searchTerm) {
          return fused.data;
        }
        return fused.fuse.search(searchTerm).map((r) => r.item);
      }),
    );
    this.driverStats$ = combineLatest([
      this.driversService.driverStats$.pipe(pluck('drivers')),
      this.currentDriver$,
    ]).pipe(
      filter(([drivers, driver]) => !!drivers && !!driver),
      map(([drivers, driver]) => {
        for (const driver1 of drivers) {
          if (driver1.UserId === driver.id) {
            return driver1;
          }
        }
        return null;
      }),
    );
    this.userInfo$ = this.authService.userInfo$;
    this.setupDriverCut();
    this.setupCoaching();
    this.redirectIfOnlyTourCompany();
  }

  public async setupCoaching() {
    const route = await this.routerState.routerState$.pipe(take(1)).toPromise();
    if (route.queryParams.coaching) {
      const stat = await this.driverStats$
        .pipe(
          filter((value) => !!value),
          take(1),
        )
        .toPromise();
      this.coachingStatus = stat.coachingCategory;
      if (stat.coachingCategory > 0) {
        this.matDialog.open(this.temp, {
          autoFocus: false,
        });
      }
    }
  }

  public async setupDriverCut() {
    const driver = await this.currentDriver$
      .pipe(
        filter((d) => !!d),
        take(1),
      )
      .toPromise();
    this.driverCutFormControl = new FormControl(driver.driverCut);
    this.cd.markForCheck();
  }

  public async promptRemoveUser() {
    this.matDialog.open(this.removeUserModal, {
      panelClass: ['w-full', 'lg:w-1/2'],
    });
  }

  public async removeUser(userId: string) {
    const success = await this.driversService.removeUser(userId);
    if (!success) {
      return;
    }

    this.snackBar.showMessage('User Removed!');
    this.router.navigate(['/', 'drivers']);
  }

  public async updateDriverPercentage() {
    const driver = await this.currentDriver$
      .pipe(
        filter((d) => !!d),
        take(1),
      )
      .toPromise();

    const value = this.driverCutFormControl.value;
    const success = await this.driversService.setDriverPercentage(driver.id, value);
    if (success) {
      this.snackBar.showMessage('Updated Driver Percentage');
    } else {
      this.snackBar.showError('Failed to update percentage');
    }
  }

  public async loadSmsMessage() {
    this.messageDetails$$.next(null);
    const driver = await this.currentDriver$.pipe(take(1)).toPromise();
    const message = await this.driversService.getDriverCoachingMessage(driver.id, +this.coachingStatus);
    this.messageDetails$$.next({
      driverPhoneNumber: driver.phoneNumber,
      suggestedText: message,
      url: `sms:${driver.phoneNumber}&body=${encodeURIComponent(message)}`,
      urlNote: this.deviceService.isMobile() ? '' : 'NOTE: text messaging must be setup on your computer',
    });
  }

  public copyText(str: string) {
    this.clipboard.copy(str);
    this.snackBar.showMessage('Copied to clipboard');
  }

  private async redirectIfOnlyTourCompany() {
    const user = await this.userInfo$.pipe(take(1)).toPromise();
    if (user.isCompanyFiveFTourOnly) {
      const backURL = await this.backUrl$.pipe(take(1)).toPromise();
      this.router.navigate(backURL);
    }
  }
}
